import { db } from '@/firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Users, CalendarCheck, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';

interface CoParentingEvent {
  id: string;
  title: string;
  startDate: string;
  type: 'handoff' | 'appointment' | 'other';
  location?: string;
}

interface CoParentingWidgetProps {
  linkId: string;
}

const CoParentingWidget: React.FC<CoParentingWidgetProps> = ({ linkId }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CoParentingEvent[]>([]);
  const [nextHandoff, setNextHandoff] = useState<CoParentingEvent | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<CoParentingEvent[]>([]);

  useEffect(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const eventsRef = collection(db, 'coParentingLinks', linkId, 'events');
    const q = query(
      eventsRef,
      where('startDate', '>=', todayStr),
      orderBy('startDate', 'asc'),
      limit(5)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsList: CoParentingEvent[] = snapshot.docs.map((doc) => doc.data() as any);
      setEvents(eventsList);

      const handoff = eventsList.find((e) => e.type === 'handoff');
      setNextHandoff(handoff || null);
      setUpcomingEvents(eventsList.slice(0, 3));
    });

    return () => unsubscribe();
  }, [linkId]);

  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'E, MMM d');
    } catch {
      return dateStr;
    }
  };

  const hasEvents = events.length > 0;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-slate-500 dark:text-slate-400" />
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Co-Parenting</h2>
      </div>

      {hasEvents ? (
        <>
          {/* Next Handoff Section */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
              <CalendarCheck className="w-4 h-4" />
              Next Handoff
            </h3>
            <div className="bg-brand/5 dark:bg-brand/10 border border-brand/20 rounded-xl p-3">
              {nextHandoff ? (
                <div className="flex flex-col">
                  <span className="text-slate-900 dark:text-slate-100 font-medium">
                    {nextHandoff.title}
                  </span>
                  <span className="text-slate-600 dark:text-slate-300">
                    {formatDate(nextHandoff.startDate)}
                  </span>
                  {nextHandoff.location && (
                    <span className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {nextHandoff.location}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-slate-500 dark:text-slate-400">No upcoming handoffs</span>
              )}
            </div>
          </div>

          {/* Upcoming Events Section */}
          <div>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">
              Upcoming
            </h3>
            <div className="space-y-2">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="flex items-start gap-2 text-sm">
                  <div className="w-1.5 h-1.5 mt-1.5 rounded-full bg-brand" />
                  <div>
                    <p className="text-slate-900 dark:text-slate-100">{event.title}</p>
                    <p className="text-slate-500 dark:text-slate-400">{formatDate(event.startDate)}</p>
                  </div>
                </div>
              ))}
              {upcomingEvents.length === 0 && (
                <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                  No other upcoming events
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="text-slate-500 dark:text-slate-400 text-sm italic py-2">
          No upcoming co-parenting events
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={() => navigate('/co-parenting')}
          className="flex items-center gap-1 text-sm font-medium text-brand hover:text-brand/80 dark:text-brand dark:hover:text-brand/80 transition-colors"
        >
          Open Co-Parenting <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CoParentingWidget;